import { Component, Input, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Transaction } from '../../model/myria/app/transaction';
import { OrderProvider } from '../../providers/order/order';
import { IOrder } from '../../model/models/order/order-interface';
import { IOrderStatusEventEntity } from '../../model/api/order/order-status-event.entity.interface';
import { OrderEventStatusType } from '../../model/types/order-event-type';
import { OrderStatusType } from '../../model/types/order-status-type';
import { LoggerProvider, LoggerEvent } from '../../providers/logger/logger';
import { WalletProvider } from '../../providers/wallet/wallet';

export interface TransferStage {
  completed: boolean;
  active: boolean;
  failed: boolean;
}

@Component({
  selector: 'rmt-transfer-status-widget',
  templateUrl: 'rmt-transfer-status-widget.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RmtTransferStatusWidgetComponent implements OnInit {
  @Input() transaction: Transaction;

  stages: TransferStage[] = [];
  loadingStages: number[] = [0, 1, 2, 3];
  isLoading: boolean = true;
  hasError: boolean = false;
  isCanceled: boolean = false;
  isRefunded: boolean = false;
  order: IOrder;

  constructor(
    private orderProvider: OrderProvider,
    private walletProvider: WalletProvider,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (!this.transaction) return;
    this.loadOrderStatus();
  }

  async loadOrderStatus() {
    try {
      this.isLoading = true;
      this.order = await this.orderProvider.getOrderStatus(this.transaction.OrderNo, false, true, false, true);
      this.buildStages();
      this.isLoading = false;
      this.cd.markForCheck();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Failed to load order status for widget');
      this.hasError = true;
      this.isLoading = false;
      this.cd.markForCheck();
    }
  }

  private getOrderEvents(): IOrderStatusEventEntity[] {
    return (this.order && this.order.events) ? this.order.events : [];
  }

  buildStages() {
    const canceledStatuses = [
      OrderStatusType.Canceled, OrderStatusType.ToCancel,
      OrderStatusType.Expired, OrderStatusType.ConfirmationExpired
    ];
    const refundedStatuses = [OrderStatusType.Refunded];

    const orderStatus = this.order ? (this.order.orderStatus || this.order.status) : null;

    if (canceledStatuses.includes(orderStatus as OrderStatusType)) {
      this.isCanceled = true;
      this.stages = this.getCanceledStages();
      return;
    }

    if (refundedStatuses.includes(orderStatus as OrderStatusType)) {
      this.isRefunded = true;
      this.stages = this.getRefundedStages();
      return;
    }

    this.stages = this.getProgressStages();
  }

  getProgressStages(): TransferStage[] {
    const events = this.getOrderEvents();
    const completedEvents = events.filter(e => e.date != null).map(e => e.event);

    const hasCreated = completedEvents.includes(OrderEventStatusType.Created);
    const hasConfirmed = completedEvents.includes(OrderEventStatusType.Confirmed);
    const hasSent = completedEvents.includes(OrderEventStatusType.Sent);
    const hasPaid = completedEvents.includes(OrderEventStatusType.Paid);

    const activeIndex = hasPaid ? 3 : hasSent ? 2 : hasConfirmed ? 1 : hasCreated ? 0 : -1;

    return [
      { completed: hasCreated, active: activeIndex === 0, failed: false },
      { completed: hasConfirmed, active: activeIndex === 1, failed: false },
      { completed: hasSent, active: activeIndex === 2, failed: false },
      { completed: hasPaid, active: activeIndex === 3, failed: false }
    ];
  }

  getCanceledStages(): TransferStage[] {
    const events = this.getOrderEvents();
    const completedEvents = events.filter(e => e.date != null).map(e => e.event);

    return [
      { completed: completedEvents.includes(OrderEventStatusType.Created), active: false, failed: false },
      { completed: false, active: false, failed: true }
    ];
  }

  getRefundedStages(): TransferStage[] {
    const events = this.getOrderEvents();
    const completedEvents = events.filter(e => e.date != null).map(e => e.event);

    return [
      { completed: completedEvents.includes(OrderEventStatusType.Created), active: false, failed: false },
      { completed: true, active: true, failed: false }
    ];
  }

  isLineCompleted(index: number): boolean {
    const next = this.stages[index + 1];
    return next ? (next.completed || next.active) : false;
  }

  isLineFailed(index: number): boolean {
    const next = this.stages[index + 1];
    return next ? next.failed : false;
  }

  openTransferDetails() {
    this.walletProvider.selectTransaction(this.transaction, 0);
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-transfer-status-widget/rmt-transfer-status-widget.ts